// Where each item of the PR header takes you: readiness chips and the
// headline to an Overview card (or the first unresolved thread), the
// meter's counts to the Files view or the Review view.
import type { ReviewThread } from '../api/queries';
import type { ReadinessItem, ReadinessStatus } from './readiness';
import { prPath } from '../router';
import { carryFilter } from './filefilter';

/** Overview side cards a header item can point at (element IDs `card-<name>`). */
export type Card = 'merge' | 'attention' | 'reviewers' | 'checks';

/** What locates a review thread in the Files view. */
export type ThreadLocation = Pick<ReviewThread, 'id' | 'path' | 'line' | 'diffSide' | 'subjectType'>;

export type HeaderTarget =
  | { kind: 'card'; card: Card }
  /** `comment`: the node ID of the comment to show, when not the thread as a whole. */
  | { kind: 'thread'; thread: ThreadLocation; comment?: string }
  | { kind: 'publish' }
  | { kind: 'unreviewed' };

/** The query parameter that scrolls the Overview to a card and highlights it. */
export const FOCUS_PARAM = 'focus';
/** The query parameter that scrolls the Files view to a thread (by node ID) and highlights it. */
export const THREAD_PARAM = 'thread';
/** With THREAD_PARAM: the comment (by node ID) in that thread to scroll to. */
export const COMMENT_PARAM = 'comment';

export const CARDS: readonly Card[] = ['merge', 'attention', 'reviewers', 'checks'];

export function isCard(v: string | null): v is Card {
  return !!v && (CARDS as readonly string[]).includes(v);
}

/** An unresolved thread anyone can see (not only the viewer's draft). */
export function isUnresolved(t: ReviewThread): boolean {
  return !t.isResolved && t.comments.nodes.some((c) => c.pullRequestReview?.state !== 'PENDING');
}

/**
 * The first unresolved thread: in the order of `files` (the PR's changed
 * files as GitHub lists them; other paths after, by name), then by line.
 */
export function firstUnresolvedThread(threads: ReviewThread[], files: string[] = []): ReviewThread | null {
  const rank = new Map(files.map((f, i) => [f, i]));
  const order = (t: ReviewThread) => rank.get(t.path) ?? files.length;
  const line = (t: ReviewThread) => t.line ?? t.originalLine ?? 0;
  const list = threads.filter(isUnresolved);
  list.sort((a, b) => order(a) - order(b) || a.path.localeCompare(b.path) || line(a) - line(b));
  return list[0] ?? null;
}

/** Where a readiness chip of a PR in `status` leads; null for one with nowhere to go. */
export function itemTarget(item: ReadinessItem, status: ReadinessStatus, firstThread: ReviewThread | null): HeaderTarget | null {
  switch (item.kind) {
    case 'decision':
    case 'approvals':
      return { kind: 'card', card: 'reviewers' };
    case 'checks':
      return { kind: 'card', card: 'checks' };
    case 'threads':
      return firstThread ? { kind: 'thread', thread: firstThread } : null;
    case 'merge':
      // A draft's merge state only repeats the state pill.
      return status === 'draft' ? null : { kind: 'card', card: 'merge' };
  }
}

/** Where the readiness headline leads (null for a draft, merged or closed PR). */
export function headlineTarget(status: ReadinessStatus, firstThread: ReviewThread | null): HeaderTarget | null {
  // Exhaustive: a new status does not compile until it has a target.
  switch (status) {
    case 'ready':
      return { kind: 'card', card: 'merge' };
    case 'unresolved':
      return firstThread ? { kind: 'thread', thread: firstThread } : null;
    case 'changesRequested':
    case 'inReview':
      return { kind: 'card', card: 'reviewers' };
    case 'draft':
    case 'merged':
    case 'closed':
      return null;
  }
}

export interface PrRef {
  owner: string;
  repo: string;
  number: number;
}

/**
 * The URL a target opens, carrying the Files view's file filter. Cards open
 * the Overview with `?focus=<card>`; a thread opens its file in the Files
 * view at its line (new side) with `?thread=<id>`. Null for targets that
 * are actions, not places (publish, unreviewed).
 */
export function targetHref(t: HeaderTarget, pr: PrRef, query: URLSearchParams): string | null {
  if (t.kind === 'card') {
    const q = new URLSearchParams({ [FOCUS_PARAM]: t.card });
    return carryFilter(prPath(pr.owner, pr.repo, pr.number) + '?' + q, query);
  }
  if (t.kind === 'thread') {
    const th = t.thread;
    const q = new URLSearchParams({ file: th.path });
    if (th.line && th.diffSide !== 'LEFT' && th.subjectType !== 'FILE') q.set('line', String(th.line));
    q.set(THREAD_PARAM, th.id);
    if (t.comment) q.set(COMMENT_PARAM, t.comment);
    return carryFilter(prPath(pr.owner, pr.repo, pr.number, 'files') + '?' + q, query);
  }
  return null;
}

const CARD_NAMES: Record<Card, string> = {
  merge: 'the merge card',
  attention: 'the attention card',
  reviewers: 'the reviewers',
  checks: 'the checks',
};

/** Accessible name for a header item that leads to `t`: its label, then where it goes. */
export function targetName(label: string, t: HeaderTarget): string {
  switch (t.kind) {
    case 'card':
      return `${label}: show ${CARD_NAMES[t.card]}`;
    case 'thread':
      return `${label}: go to the first unresolved thread (${t.thread.path})`;
    case 'publish':
      return `${label}: go to Review, to send your review`;
    case 'unreviewed':
      return `${label}: show unreviewed files`;
  }
}

/**
 * The first changed file, in `paths` order, that still has unreviewed
 * blocks, preferring files after `current` (wrapping around); null when
 * all are reviewed.
 */
export function nextUnreviewedFile(
  paths: string[],
  progress: (path: string) => { total: number; done: number },
  current: string | null,
): string | null {
  const open = (p: string) => {
    const s = progress(p);
    return s.done < s.total;
  };
  const at = current ? paths.indexOf(current) : -1;
  if (current && at >= 0 && open(current)) return current;
  const ordered = at < 0 ? paths : [...paths.slice(at + 1), ...paths.slice(0, at + 1)];
  return ordered.find(open) ?? null;
}

/** How long the Files view waits for a thread it was sent to (`?thread=`) to render. */
export const JUMP_WAIT_MS = 8000;
/** How long it then waits for the comment named (`?comment=`) to show in the thread. */
export const COMMENT_WAIT_MS = 1500;

/** What the Files view does next on its way to a thread: keep waiting, show `target`, or stop. */
export type JumpStep<T> = { kind: 'wait' } | { kind: 'show'; target: T } | { kind: 'give-up' };

/**
 * One step of the jump to a thread. While the comparison the link names is
 * not yet on screen (`comparisonShown`), it waits without a deadline: the
 * thread found then is in the diff being replaced. It then waits for the
 * thread to render and for a line deep link's own scroll to finish
 * (`settling`), and gives up after JUMP_WAIT_MS (`elapsed`, counted from
 * when the comparison was shown). With a comment named (`wantComment`), it
 * shows that comment once it is there; when it does not appear within
 * COMMENT_WAIT_MS of the thread (`threadElapsed`), or by the overall
 * deadline, it shows the thread instead, so the jump always lands.
 */
export function jumpStep<T>(s: {
  thread: T | null;
  comment: T | null;
  wantComment: boolean;
  comparisonShown: boolean;
  settling: boolean;
  elapsed: number;
  threadElapsed: number | null;
}): JumpStep<T> {
  if (!s.comparisonShown) return { kind: 'wait' };
  if (!s.thread || s.settling) return s.elapsed < JUMP_WAIT_MS ? { kind: 'wait' } : { kind: 'give-up' };
  if (s.comment) return { kind: 'show', target: s.comment };
  if (s.wantComment && (s.threadElapsed ?? 0) < COMMENT_WAIT_MS && s.elapsed < JUMP_WAIT_MS) return { kind: 'wait' };
  return { kind: 'show', target: s.thread };
}
